import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Input from '../../../components/Input';
import Service from '../../../components/Systema/Service';
import config from '../../../config';

const service = new Service(`${config.api_path}/v1/estrutura-organizacional`);

function buildPath(value) {
  const path = [];
  let current = value;

  while (current) {
    path.unshift(current);
    current = current.estruturaOrganizacionalPai;
  }

  return path;
}

function buildRootFilter() {
  return {
    estruturaOrganizacionalPai: {
      compare: '=',
      value: 'null',
    },
  };
}

function buildChildrenFilter(parent) {
  if (!parent) {
    return buildRootFilter();
  }

  return {
    'estruturaOrganizacionalPai.id': {
      compare: '=',
      value: `long:${parent.id}`,
    },
  };
}

function formatNode(node) {
  if (!node) {
    return '';
  }

  return node.codigoCompleto ? `${node.codigoCompleto} - ${node.nome}` : `${node.codigo} - ${node.nome}`;
}

export default function HierarchicalParentSelect({
  id = 'estruturaOrganizacionalPai',
  name = 'estruturaOrganizacionalPai',
  value,
  onChange,
  isInvalid = false,
  placeholder = 'Selecione a estrutura imediatamente superior',
}) {
  const [selectedPath, setSelectedPath] = useState([]);
  const [canSelectChild, setCanSelectChild] = useState(false);
  const requestRef = useRef(0);

  async function syncChildrenAvailability(node) {
    if (!node) {
      setCanSelectChild(false);
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    try {
      const response = await service.list({
        limit: 1,
        offset: 0,
        filter: buildChildrenFilter(node),
      });

      if (requestRef.current !== requestId) {
        return;
      }

      setCanSelectChild(response.data.list.length > 0);
    } catch (error) {
      if (requestRef.current === requestId) {
        setCanSelectChild(false);
      }
    }
  }

  useEffect(() => {
    const path = buildPath(value);
    setSelectedPath(path);
    syncChildrenAvailability(path[path.length - 1] || null);
  }, [value]);

  function propagateChange(path) {
    const selectedNode = path[path.length - 1] || null;

    if (onChange) {
      onChange({
        target: {
          id,
          name,
          value: selectedNode,
        },
      });
    }
  }

  function handleLevelChange(level, selected) {
    const nextPath = selected ? [...selectedPath.slice(0, level), selected] : selectedPath.slice(0, level);
    setSelectedPath(nextPath);
    propagateChange(nextPath);
    syncChildrenAvailability(nextPath[nextPath.length - 1] || null);
  }

  const levels = useMemo(() => {
    if (selectedPath.length === 0) {
      return [{ parent: null, value: null, label: 'Raiz' }];
    }

    const mappedLevels = selectedPath.map((node, index) => ({
      parent: index === 0 ? null : selectedPath[index - 1],
      value: node,
      label: index === 0 ? 'Raiz' : `Nível ${index + 1}`,
    }));

    if (canSelectChild) {
      mappedLevels.push({
        parent: selectedPath[selectedPath.length - 1],
        value: null,
        label: `Nível ${selectedPath.length + 1}`,
      });
    }

    return mappedLevels;
  }, [canSelectChild, selectedPath]);

  return (
    <div>
      {levels.map((level, index) => (
        <div key={`${level.parent ? level.parent.id : 'root'}-${index}`} className={index > 0 ? 'mt-2' : ''}>
          {index > 0 && (
            <Form.Text className="text-muted">
              {level.label}
            </Form.Text>
          )}
          <Input.AsyncSelect
            id={`${id}-${index}`}
            value={level.value}
            onChange={(event) => handleLevelChange(index, event.target.value)}
            isInvalid={isInvalid}
            path={`${config.api_path}/v1/estrutura-organizacional`}
            label={`\${codigo} - \${nome}`}
            rowKey="id"
            size="sm"
            nullable
            placeholder={index === 0 ? placeholder : 'Selecione uma subestrutura'}
            dataFilter={buildChildrenFilter(level.parent)}
          />
        </div>
      ))}

      {selectedPath.length > 0 && (
        <Form.Text className="text-muted">
          Selecionado: {selectedPath.map(formatNode).join(' / ')}
        </Form.Text>
      )}
    </div>
  );
}
